import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";
const INNAE_DEMO_ALIASES = ["innae"];

interface InnaeProps {
  selectedBelt: string;
  getBeltColor: (belt: string) => string;
  techniques: string[];
  /**
   * `curriculum` = demo link + horizontal tabs (main curriculum tab).
   * `legacy` (default) = accordion (dropdown).
   */
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
}

/** Stable Radix tab value for each technique id from `getInnaeTechniques`. */
export function innaeTechniqueToTabValue(technique: string): string {
  if (technique === "Innae In-Eum") return "in-eum";
  if (technique === "Innae In-Jang") return "in-jang";
  return technique;
}

/** Short label for curriculum tab strip: 1–9, In-Eum, In-Jang. */
function innaeTabLabel(technique: string): string {
  if (technique === "Innae In-Eum") return "In-Eum";
  if (technique === "Innae In-Jang") return "In-Jang";
  return technique;
}

/** Same labels as the selectable tabs — shown in Required for Grading. */
function formatInnaeGradingRequirement(techniques: string[]): string {
  return techniques.map((t) => innaeTabLabel(t)).join(", ");
}

function formatAccordionTriggerTitle(technique: string): string {
  if (technique === "Innae In-Eum") return "Innae In-Eum (인내 인음)";
  if (technique === "Innae In-Jang") return "Innae In-Jang (인내 인장)";
  return `Innae ${technique} (인내 ${technique})`;
}

function InstructionLine({
  instruction,
  index,
}: {
  instruction: string;
  index: number;
}) {
  return (
    <li className="flex gap-2">
      <span className="shrink-0 font-semibold text-taekwondo-red tabular-nums">
        {index + 1}.
      </span>
      <span className="text-gray-700">
        {instruction.includes("KIHAP") ? (
          <>
            {instruction.replace(" KIHAP", "")}{" "}
            <span className="font-semibold">KIHAP</span>
          </>
        ) : (
          instruction
        )}
      </span>
    </li>
  );
}

/** Plain instruction line (no KIHAP bolding). */
function PlainInstructionLine({
  instruction,
  index,
}: {
  instruction: string;
  index: number;
}) {
  return (
    <li className="flex gap-2">
      <span className="shrink-0 font-semibold text-taekwondo-red tabular-nums">
        {index + 1}.
      </span>
      <span className="text-gray-700">{instruction}</span>
    </li>
  );
}

/** Shared body copied from legacy accordion — one block per technique. */
export function InnaeTechniqueBody({ technique }: { technique: string }) {
  if (technique === "Innae In-Jang") {
    return (
      <div className="space-y-2 text-gray-700">
        <ol className="list-none space-y-2">
          {[
            "4 directional moving right leg back inbetween sets.",
            "1 faces front then 2 L followed by R",
            "R leg moves back for 3 to face the front then dora to back.",
            "Only dora turn different is 5 where you step forward & turn opposite way.",
            "9 is also different between sides - right leg goes backwards 180° to face the front for the final 2nd half of the set",
          ].map((instruction, index) => (
            <PlainInstructionLine
              key={`in-jang-${index}`}
              instruction={instruction}
              index={index}
            />
          ))}
        </ol>
      </div>
    );
  }

  if (technique === "Innae In-Eum") {
    return (
      <div className="space-y-2 text-gray-700">
        <ol className="list-none space-y-2">
          {["Steps 1-9 in a fluid motion with no Joonbi between forms"].map(
            (instruction, index) => (
              <PlainInstructionLine
                key={index}
                instruction={instruction}
                index={index}
              />
            ),
          )}
        </ol>
      </div>
    );
  }

  if (technique === "1") {
    return (
      <div className="space-y-2 text-gray-700">
        <ol className="list-none space-y-2">
          {[
            "Cover across body with right hand stepping right leg into Horse Stance left middle punch KIHAP",
            "Right middle punch KIHAP",
            "2 x high punches (L/R) KIHAP on 2nd",
            "Triple punch (2 mid (L/R), 1 L high KIHAP)",
            "Repeat triple punches 2 mid R/L, 1 R high KIHAP",
          ].map((instruction, index) => (
            <InstructionLine
              key={index}
              instruction={instruction}
              index={index}
            />
          ))}
        </ol>
      </div>
    );
  }

  if (technique === "2") {
    return (
      <div className="space-y-2 text-gray-700">
        <ol className="list-none space-y-2">
          {[
            "Step right leg back Back Stance down block",
            "Adjust front leg to Front Stance mid section punch KIHAP",
            "Repeat other side",
          ].map((instruction, index) => (
            <InstructionLine
              key={index}
              instruction={instruction}
              index={index}
            />
          ))}
        </ol>
      </div>
    );
  }

  if (technique === "3") {
    return (
      <div className="space-y-2 text-gray-700">
        <ol className="list-none space-y-2">
          {[
            "Step right leg back Back Stance rising block",
            "Adjust front leg to Front Stance high section punch KIHAP",
            "Repeat other side",
          ].map((instruction, index) => (
            <InstructionLine
              key={index}
              instruction={instruction}
              index={index}
            />
          ))}
        </ol>
      </div>
    );
  }

  if (technique === "4") {
    return (
      <div className="space-y-2 text-gray-700">
        <ol className="list-none space-y-2">
          {[
            "Step right leg back Cat Stance inner forearm outer block with front arm L",
            "Keep stance mid section punch KIHAP",
            "Repeat other side",
          ].map((instruction, index) => (
            <InstructionLine
              key={index}
              instruction={instruction}
              index={index}
            />
          ))}
        </ol>
      </div>
    );
  }

  if (technique === "5") {
    return (
      <div className="space-y-2 text-gray-700">
        <ol className="list-none space-y-2">
          {[
            "Step right leg back Back Stance single knifehand block (L)",
            "Adjust front leg to Front Stance reverse knifehand strike (R) KIHAP",
            "Repeat other side",
          ].map((instruction, index) => (
            <InstructionLine
              key={index}
              instruction={instruction}
              index={index}
            />
          ))}
        </ol>
      </div>
    );
  }

  if (technique === "6") {
    return (
      <div className="space-y-2 text-gray-700">
        <ol className="list-none space-y-2">
          {[
            "Step right leg back Back Stance knifehand guarding block",
            "Adjust to tiger stance palm heel strike (R) KIHAP",
            "Repeat other side",
          ].map((instruction, index) => (
            <InstructionLine
              key={index}
              instruction={instruction}
              index={index}
            />
          ))}
        </ol>
      </div>
    );
  }

  if (technique === "7") {
    return (
      <div className="space-y-2 text-gray-700">
        <ol className="list-none space-y-2">
          {[
            "Step right leg back Back Stance inner hammer block (L)",
            "Double mid punch (R/L) KIHAP on 2nd punch",
            "Grabbing circular motion face height L hand",
            "Adjust front leg to Front Stance high section punch (R) KIHAP",
            "Repeat other side",
          ].map((instruction, index) => (
            <InstructionLine
              key={index}
              instruction={instruction}
              index={index}
            />
          ))}
        </ol>
      </div>
    );
  }

  if (technique === "8") {
    return (
      <div className="space-y-2 text-gray-700">
        <ol className="list-none space-y-2">
          {[
            "Step R leg back Back Stance downward KH guarding block",
            "Adjust to tiger stance lead long 45* punch KIHAP with rear arm knifehand cover at face level",
            "Adjust back into cat stance lead arm downward parry & rear arm reverse mid punch KIHAP",
            "Open front hand motioning across body blocking palm up",
            "Adjust to Front Stance reverse high punch KIHAP",
            "Repeat other side",
          ].map((instruction, index) => (
            <InstructionLine
              key={index}
              instruction={instruction}
              index={index}
            />
          ))}
        </ol>
      </div>
    );
  }

  if (technique === "9") {
    return (
      <div className="space-y-2 text-gray-700">
        <ol className="list-none space-y-2">
          {[
            "Step R leg back Back Stance double knife hand block (rising & outer forearm knife block)",
            "Cover (rear arm low palm out, lead arm palm out side of face)",
            "R leg step over left into X stance with L knife hand strike KIHAP & R arm catching opponents leg at shoulder height (keep R arm in this upper L position until end)",
            "L leg low sweep",
            "Land & step R leg forward with low palm heel strike KIHAP in low Tiger Stance",
            "Step back & repeat other side",
            "At end of 2nd side drop rear knee to floor tension",
          ].map((instruction, index) => (
            <InstructionLine
              key={index}
              instruction={instruction}
              index={index}
            />
          ))}
        </ol>
      </div>
    );
  }

  return (
    <p className="text-gray-700">
      Detailed instructions for Innae {technique} will be available soon.
    </p>
  );
}

function InnaeLegacyLayout({ techniques }: { techniques: string[] }) {
  return (
    <Accordion type="single" collapsible className="w-full space-y-2">
      {techniques.map((technique, index) => (
        <AccordionItem key={`innae-${index}`} value={`innae-${technique}`}>
          <AccordionTrigger className="rounded-lg bg-gray-50 px-4 py-2 hover:bg-gray-100 hover:no-underline">
            <div className="flex w-full items-center text-left">
              <span className="flex-1">
                {formatAccordionTriggerTitle(technique)}
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-0">
            <div className="mt-2 rounded-lg bg-gray-50 p-4">
              <div className="space-y-4">
                <InnaeTechniqueBody technique={technique} />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

function InnaeCurriculumLayout({
  techniques,
  hasDemo,
  onWatchDemo,
  beltTabSurfaceClass,
}: {
  techniques: string[];
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  beltTabSurfaceClass: string;
}) {
  const techniquesFingerprint = techniques.join("|");

  const [activeTab, setActiveTab] = useState(() =>
    innaeTechniqueToTabValue(techniques[0] ?? "1"),
  );

  useEffect(() => {
    setActiveTab(innaeTechniqueToTabValue(techniques[0] ?? "1"));
  }, [techniquesFingerprint]);

  const surface = beltTabSurfaceClass;

  const gradingSummary = formatInnaeGradingRequirement(techniques);

  return (
    <div className="space-y-6 text-left">
      <section className="rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5">
        <h3 className="mb-2 font-semibold text-lg text-taekwondo-red">
          Required for Grading
        </h3>
        <p className="text-gray-800">{gradingSummary}</p>
      </section>

      {hasDemo?.(INNAE_DEMO_ALIASES) && (
        <div>
          <button
            type="button"
            className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
            onClick={() => onWatchDemo?.(INNAE_DEMO_ALIASES)}
          >
            Watch Demonstration Video
          </button>
        </div>
      )}

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full space-y-4"
      >
        <ScrollableCurriculumTabStrip
          key={techniquesFingerprint}
          ariaLabelPrefix="Innae forms"
        >
          {techniques.map((technique) => {
            const v = innaeTechniqueToTabValue(technique);
            return (
              <TabsTrigger
                key={v}
                value={v}
                className={cn(
                  "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium tabular-nums border border-black/10 transition-shadow",
                  surface,
                  "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
                )}
              >
                {innaeTabLabel(technique)}
              </TabsTrigger>
            );
          })}
        </ScrollableCurriculumTabStrip>

        {techniques.map((technique) => {
          const v = innaeTechniqueToTabValue(technique);
          return (
            <TabsContent
              key={v}
              value={v}
              className="mt-0 focus-visible:outline-none"
            >
              <div className="mt-2 rounded-lg bg-gray-50 p-4 md:p-5">
                <InnaeTechniqueBody technique={technique} />
              </div>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}

export default function Innae({
  selectedBelt: _selectedBelt,
  getBeltColor: _getBeltColor,
  techniques,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
  hasDemo,
  onWatchDemo,
}: InnaeProps) {
  if (techniques.length === 0) return null;

  if (presentation === "curriculum") {
    return (
      <InnaeCurriculumLayout
        techniques={techniques}
        hasDemo={hasDemo}
        onWatchDemo={onWatchDemo}
        beltTabSurfaceClass={beltTabSurfaceClass}
      />
    );
  }

  return <InnaeLegacyLayout techniques={techniques} />;
}
